import { ReactNode, useState } from 'react';
import { Tooltip, Typography } from '@mui/material';

import { colors } from 'crono-fe-common/theme';
import { StrategyStep } from 'crono-fe-common/types/strategyStep';
import { StrategyStepTemplateWrapper } from './style';
import { FlexDiv } from 'crono-fe-common/components/Layout/FlexDiv';

import { StrategyStepTemplate } from 'crono-fe-common/types/DTO/strategyStepTemplates';
import TemplateType from 'crono-fe-common/types/enums/templateType';
import SelectedTemplateIcon from 'crono-fe-common/icons/Icon-Selected-Template';
import CloseMIcon from 'crono-fe-common/icons/Icon-Close';
import { FeConstants } from 'constants/FeConstants';
import { ReactComponent as StarIcon } from '../../../assets/images/Blue_star.svg';
import { useConfirmModal } from 'context/confirmModal';
import EditableTemplate from '../stepDetailComponent/editableTemplate';
import EditPencilIconS from 'crono-fe-common/icons/Icon-Edit-Pencil-S';
import CronoSwitch from 'crono-fe-common/components/CronoSwitch';
import { TemplateCardsContainerTooltip } from 'pages/accountDrawer/linkedinView/templateCardsContainerTooltip';
import { StrategyConditionalStep } from 'crono-fe-common/types/DTO/strategyConditionalStep';
import type { HandleAllChangesStrategyStepTemplateType } from '../strategyDetailModal/strategyDetailModal';
import { useStrategyNavigation } from '../useStrategyNavigation';

interface IProps {
  strategyStepTemplate: StrategyStepTemplate;
  stepOrConditionalStep: StrategyStep | StrategyConditionalStep;
  stepId: number;
  stepConditionalId?: number;
  handleChange: (
    type: HandleAllChangesStrategyStepTemplateType,
    payload: {
      templateIndex: number;
      enabled?: boolean;
      strategyStepTemplateId: number;
    },
    index: number,
  ) => void;
  index: number;
  currentIndex: number;
  strategyStepTemplates: StrategyStepTemplate[];
  onSuccessEditTemplate: (
    data:
      | {
          newInsertedTemplateId: number;
          isAbTesting: boolean;
        }
      | undefined,
  ) => void;
  // templates tooltip
  templateComponent?: JSX.Element | null;
  closeTemplateTooltip?: () => void;
  showTemplateTab?: boolean;
  EmailThreadButton?: ReactNode;
  previewOnly?: boolean;
}

const ABTestingTemplates = ({
  strategyStepTemplate,
  stepOrConditionalStep,
  stepId,
  stepConditionalId,
  handleChange,
  index,
  currentIndex,
  strategyStepTemplates,
  onSuccessEditTemplate,
  templateComponent,
  closeTemplateTooltip,
  showTemplateTab,
  EmailThreadButton,
  previewOnly = false,
}: IProps) => {
  const { openModal: openConfirmModal } = useConfirmModal();

  const { id: strategyId } = useStrategyNavigation();

  const handleDeleteStrategyStepTemplate = () => {
    // if (checkEnabledTemplates(step) > 1) {
    openConfirmModal({
      title: 'Are you sure you want to delete this template?',
      text: 'If you delete the attached template, then all statistics will be removed and you will not be able to resume it',
      cancelText: 'Cancel',
      confirmText: 'Delete',
      confirmFunction: () =>
        handleChange(
          'delete',
          {
            templateIndex: currentIndex,
            strategyStepTemplateId: strategyStepTemplate.id,
          },
          index,
        ),
      cancelFunction: () => {
        return;
      },
    });
    // }
  };

  const checkEnabledTemplates = (
    step: StrategyStep | StrategyConditionalStep,
  ) => {
    return step.strategyStepTemplates!.filter((template) => template.enabled)
      .length;
  };

  const renderAnalytics = (template: StrategyStepTemplate) => {
    const countTotal = template.useCount;
    const openPercentage = Math.round((template.openCount / countTotal) * 100);
    const clickPercentage = Math.round(
      (template.clickCount / countTotal) * 100,
    );
    const replyPercentage = Math.round(
      (template.replyCount / countTotal) * 100,
    );

    return (
      <div className="ab-testing-analytics-container">
        <div
          className={`ab-analytics useCount ${
            !template.enabled ? 'disabled-ab-template' : ''
          }`}
        >
          <Typography fontSize={12} fontWeight={500}>
            {countTotal}
          </Typography>
          <Typography fontSize={12} fontWeight={500} color={colors.grey11}>
            Usage
          </Typography>
        </div>
        {template.type === TemplateType.EMAIL && (
          <>
            <div
              className={`ab-analytics openCount ${
                !template.enabled ? 'disabled-ab-template' : ''
              }`}
            >
              <Typography fontSize={12} fontWeight={500}>
                {countTotal ? openPercentage : '0'} %
              </Typography>
              <Typography fontSize={12} fontWeight={500} color={colors.grey11}>
                Open
              </Typography>
            </div>
            <div
              className={`ab-analytics clickCount ${
                !template.enabled ? 'disabled-ab-template' : ''
              }`}
            >
              <Typography fontSize={12} fontWeight={500}>
                {countTotal ? clickPercentage : '0'} %
              </Typography>
              <Typography fontSize={12} fontWeight={500} color={colors.grey11}>
                Clicks
              </Typography>
            </div>
          </>
        )}
        <div
          className={`ab-analytics replyCount ${
            !template.enabled ? 'disabled-ab-template' : ''
          }`}
        >
          <Typography fontSize={12} fontWeight={500}>
            {countTotal ? replyPercentage : '0'} %
          </Typography>
          <Typography fontSize={12} fontWeight={500} color={colors.grey11}>
            {template.type === TemplateType.INVITATION ? 'Accepted' : 'Reply'}
          </Typography>
        </div>
      </div>
    );
  };

  const assignBadge = (
    abTemplate: StrategyStepTemplate,
    strategyStepTemplates: StrategyStepTemplate[],
  ) => {
    const orderedSequenceSteps = strategyStepTemplates.sort((a, b) => {
      if (a.useCount >= 10 && b.useCount < 10) return -1;
      if (a.useCount < 10 && b.useCount >= 10) return 1;
      return a.replyCount > b.replyCount ? -1 : 1;
    });

    if (
      abTemplate.id === orderedSequenceSteps[0].id &&
      abTemplate.useCount >= 10 &&
      abTemplate.replyCount >= 1
    ) {
      return (
        <div className="best-conversion-container">
          <StarIcon />
          <Typography
            fontSize={'12px'}
            fontWeight={500}
            color={colors.intenseBlue}
            textOverflow={'ellipsis'}
            overflow={'hidden'}
            whiteSpace={'nowrap'}
          >
            Best conversion
          </Typography>
        </div>
      );
    } else {
      return null;
    }
  };

  const isEmail = strategyStepTemplate.type === TemplateType.EMAIL;

  const [observerEditMode, setObserverEditMode] = useState(false);

  return (
    <StrategyStepTemplateWrapper>
      <FlexDiv
        justifyContent="start"
        alignItems="start"
        direction="column"
        style={{
          padding: '8px',
          width: undefined,
          flex: 1,
        }}
      >
        <div className="ab-testing-header">
          <div style={{ padding: '0px 0px 0px 8px' }}>
            <Tooltip
              arrow
              placement="top"
              title={
                !previewOnly &&
                strategyStepTemplate.enabled &&
                checkEnabledTemplates(stepOrConditionalStep) === 1
                  ? 'You must have at least 1 template enabled'
                  : null
              }
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  border: `1px solid ${colors.grey444}`,
                  borderRadius: '8px',
                  paddingRight: '8px',
                }}
              >
                <CronoSwitch
                  checked={strategyStepTemplate.enabled}
                  disabled={
                    previewOnly ||
                    (strategyStepTemplate.enabled &&
                      checkEnabledTemplates(stepOrConditionalStep) === 1) ||
                    observerEditMode
                  }
                  onChange={() => {
                    handleChange(
                      'enabled',
                      {
                        templateIndex: currentIndex,
                        enabled: !strategyStepTemplate.enabled,
                        strategyStepTemplateId: strategyStepTemplate.id,
                      },
                      index,
                    );
                  }}
                />

                <Typography fontSize={14} fontWeight={500} lineHeight={'18px'}>
                  {FeConstants.abTestingOptions.charAt(currentIndex)}:
                </Typography>
              </div>
            </Tooltip>
          </div>

          <div className="flex-center" style={{ overflow: 'hidden', flex: 1 }}>
            <div
              className={`flex-center ${
                observerEditMode
                  ? 'disable-due-to-edit'
                  : !strategyStepTemplate.enabled
                    ? 'disabled-ab-template-title'
                    : 'selected-template'
              }`}
            >
              <TemplateCardsContainerTooltip
                title={templateComponent}
                onClose={closeTemplateTooltip}
                open={showTemplateTab}
                disableFocusListener
                disableHoverListener
                disableTouchListener
              >
                <>
                  <span className="flex-center icon-wrapper">
                    <SelectedTemplateIcon
                      className={
                        observerEditMode
                          ? 'step-component-button-disabled'
                          : !strategyStepTemplate.enabled || previewOnly
                            ? 'ab-testing-step-component-button-disabled'
                            : 'step-component-button'
                      }
                      color={colors.grey11}
                    />
                  </span>

                  <Typography
                    fontSize={14}
                    fontWeight={500}
                    noWrap
                    color={previewOnly ? colors.grey11 : undefined}
                    onClick={() => {
                      if (previewOnly) return;

                      // set some sort of function here
                      handleChange(
                        'template',
                        {
                          templateIndex: currentIndex,
                          strategyStepTemplateId: strategyStepTemplate.id,
                        },
                        index,
                      );
                    }}
                  >
                    {strategyStepTemplate.template?.title}
                  </Typography>
                </>
              </TemplateCardsContainerTooltip>

              {!previewOnly && (
                <span
                  onClick={() => {
                    handleDeleteStrategyStepTemplate();
                  }}
                  style={{
                    cursor: observerEditMode ? 'default' : 'pointer',
                    display: 'flex',
                  }}
                  className="close-template-option"
                >
                  <CloseMIcon color={colors.grey11} />
                </span>
              )}
            </div>

            {observerEditMode && !previewOnly && (
              <div
                className="badge-observable-mode"
                style={{ marginLeft: '10px' }}
              >
                <EditPencilIconS
                  color={colors.lightPinkHover}
                  className="pencil-icon"
                />
                Draft
              </div>
            )}

            {assignBadge(strategyStepTemplate, strategyStepTemplates)}
          </div>

          {renderAnalytics(strategyStepTemplate)}
        </div>

        {strategyStepTemplate.template && (
          <EditableTemplate
            previewOnly={previewOnly}
            emailThreadButton={isEmail ? EmailThreadButton : undefined}
            enableSubjectMutation={!stepOrConditionalStep.replyToThread}
            subject={strategyStepTemplate.template?.subject}
            stepId={stepId}
            conditionalStepId={stepConditionalId}
            isConditional={!!stepConditionalId}
            isABTesting={true}
            template={strategyStepTemplate.template}
            setObserverEditMode={(value) => setObserverEditMode(value)}
            onSuccess={onSuccessEditTemplate}
            uniqueIdentifierOfTemplate={strategyStepTemplate.id}
            strategyId={strategyId!}
          />
        )}
      </FlexDiv>
    </StrategyStepTemplateWrapper>
  );
};

export default ABTestingTemplates;
